import { Zap, Shield, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'

const QuickAddForm = ({ 
    quickAddData, 
    handleQuickAddChange, 
    handleQuickAddRoleChange, 
    roles, 
    errors 
}) => {
    const [showPassword, setShowPassword] = useState(false)
    
    return (
        <div className="max-w-2xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                    <Zap className="h-5 w-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-green-800">Quick Add Mode</h3>
                </div>
                <p className="text-sm text-green-700">
                    Add multiple cameras quickly by providing just the essential NVR details. 
                    Cameras will be created automatically in sequence (101, 201, 301, etc.).
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Location */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location Name *
                    </label>
                    <input
                        type="text"
                        name="locationName"
                        value={quickAddData.locationName}
                        onChange={handleQuickAddChange}
                        className={`input w-full ${errors.locationName ? 'border-red-500' : ''}`}
                        placeholder="e.g., KURWA, DUMKA, PAKUR"
                    />
                    {errors.locationName && (
                        <p className="text-red-500 text-xs mt-1">{errors.locationName}</p>
                    )}
                </div>

                {/* Camera Count */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Number of Cameras *
                    </label>
                    <input
                        type="number"
                        name="cameraCount"
                        value={quickAddData.cameraCount}
                        onChange={handleQuickAddChange}
                        className={`input w-full ${errors.cameraCount ? 'border-red-500' : ''}`}
                        placeholder="15"
                        min="1"
                        max="50"
                    />
                    {errors.cameraCount && (
                        <p className="text-red-500 text-xs mt-1">{errors.cameraCount}</p>
                    )}
                </div>

                {/* NVR IP */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        NVR IP Address *
                    </label>
                    <input
                        type="text"
                        name="nvrIp"
                        value={quickAddData.nvrIp}
                        onChange={handleQuickAddChange}
                        className={`input w-full ${errors.nvrIp ? 'border-red-500' : ''}`}
                        placeholder="192.168.1.100"
                    />
                    {errors.nvrIp && (
                        <p className="text-red-500 text-xs mt-1">{errors.nvrIp}</p>
                    )}
                </div>

                {/* NVR Port */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        NVR RTSP Port *
                    </label>
                    <input
                        type="number"
                        name="nvrPort"
                        value={quickAddData.nvrPort}
                        onChange={handleQuickAddChange}
                        className={`input w-full ${errors.nvrPort ? 'border-red-500' : ''}`}
                        placeholder="554"
                        min="1"
                        max="65535"
                    />
                    {errors.nvrPort && (
                        <p className="text-red-500 text-xs mt-1">{errors.nvrPort}</p>
                    )}
                </div>

                {/* Username */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username *
                    </label>
                    <input
                        type="text"
                        name="nvrUsername"
                        value={quickAddData.nvrUsername}
                        onChange={handleQuickAddChange}
                        className={`input w-full ${errors.nvrUsername ? 'border-red-500' : ''}`}
                        placeholder="admin"
                    />
                    {errors.nvrUsername && (
                        <p className="text-red-500 text-xs mt-1">{errors.nvrUsername}</p>
                    )}
                </div>

                {/* Password */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Password *
                    </label>
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="nvrPassword"
                            value={quickAddData.nvrPassword}
                            onChange={handleQuickAddChange}
                            className={`input w-full pr-10 ${errors.nvrPassword ? 'border-red-500' : ''}`}
                            placeholder="Enter password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                            ) : (
                                <Eye className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                    {errors.nvrPassword && (
                        <p className="text-red-500 text-xs mt-1">{errors.nvrPassword}</p>
                    )}
                </div>
            </div>

            {/* Role Assignment for Quick Add */}
            <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2" />
                    Access Control
                </h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assign to Roles *
                        </label>
                        <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-3">
                            {roles.map((role) => (
                                <label key={role._id} className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        checked={quickAddData.accessRoles.includes(role._id)}
                                        onChange={() => handleQuickAddRoleChange(role._id)}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700">{role.name}</span>
                                </label>
                            ))}
                        </div>
                        {errors.accessRoles && (
                            <p className="text-red-500 text-xs mt-1">{errors.accessRoles}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Preview */}
            <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Preview:</h4>
                <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Location:</strong> {quickAddData.locationName || 'Not set'}</p>
                    <p><strong>NVR:</strong> {quickAddData.nvrIp || 'Not set'}:{quickAddData.nvrPort || 'Not set'}</p>
                    <p><strong>Credentials:</strong> {quickAddData.nvrUsername || 'Not set'}:••••••••</p>
                    <p><strong>Cameras to create:</strong> {quickAddData.cameraCount || 0}</p>
                    <p><strong>Channel sequence:</strong> {quickAddData.startChannel}, {quickAddData.startChannel + quickAddData.channelIncrement}, {quickAddData.startChannel + (quickAddData.channelIncrement * 2)}...</p>
                </div>
            </div>
        </div>
    )
}

export default QuickAddForm 